import {
  map,
  withLatestFrom,
  filter,
  mapTo,
  observeOn,
  switchMap,
  mergeMap
} from 'rxjs/operators'
import { asyncScheduler, of } from 'rxjs'
import { ofType } from 'redux-observable'
import { history } from '../store'
import Actions from '../actions'
import { getPayload } from '../utils/rxUtils'
import { clinicTypes, getContactSupportUrl, routes } from '../consts'
import config from '../config.json'
import { hasAuthToken, isLoggedIn } from '../utils/fetchUtils'
import validateZipcode from '../utils/validateZipcode'
import {
  filterQueueAppointmentsClinic,
  filterPendingInitialAppointment,
  isNeedToAskMyPharmacyUpdate
} from '../utils/appointmentsUtils'
import { uriEncodeSearchParams } from '../utils/encodingUtils'
import { MenuRoutes } from '../consts/Menu'
import { convertClinicNames } from '../utils/clinicsUtils'
import { getIsHmoMember } from '../utils/hmoUtils'

const dispatchNavigatedAction = map(payload =>
  Actions.createAction(Actions.NAVIGATION_CHANGED, payload)
)

export const navigateBack = action$ =>
  action$.pipe(
    getPayload(Actions.NAVIGATED_BACK),
    map(() => {
      history.goBack()
    }),
    dispatchNavigatedAction
  )

const isBotRoute = path => path === routes.BOT || path === routes.GET_CARE
const isSignupRoute = path => path.includes(routes.SIGN_UP)

// this epic handles navigated to action - push the path to router
// we should block this from happen just in case the user is in the middle of bot intake (TERMINATE_BOT_POPUP visible)
// related to 'blockNavigateOutsideBot' Epic
export const navigateTo = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.NAVIGATED_TO),
    withLatestFrom(state$),
    filter(
      ([payload, { router }]) =>
        !isBotRoute(router?.location?.pathname) &&
        !(
          router?.location?.pathname === routes.RISK_ASSESSMENT_BOT &&
          payload !== routes.PROFILE_ROUTE.MVP_STATUS
        )
    ), // only for !NOT! bot routes!,
    // Put on micro task queue, so we let the appnavigator update b4 changing route
    observeOn(asyncScheduler),
    map(([payload, _]) => {
      history.push(payload)
    }),
    dispatchNavigatedAction
  )
}

// on bot we should show alert the user when he navigate somewhere outside the bot
// this is in order to prevent him for loosing data/leave in the middle of the process intake
// when we get navigation action from bot, we should block navigation (epic navigateTo) and dispatch terminate popup action
export const blockNavigateOutsideBot = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.NAVIGATED_TO),
    withLatestFrom(state$),
    filter(([_, { router }]) => isBotRoute(router?.location?.pathname)), // only for bot routes!,
    switchMap(([payload, _]) =>
      of(
        Actions.createAction(Actions.TERMINATE_BOT_POPUP, {
          isVisible: true,
          navigationPath: payload
        })
      )
    )
  )
}

// NAVIGATION_FROM_BOT is like NAVIGATION_TO action, just happens from bot
// when navigation from bot when ended, exit point etc
// this navigation is not blocked and shouldn't display the bot end alert
export const navigationToFromBot = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.NAVIGATION_FROM_BOT),
    withLatestFrom(state$),
    // Put on micro task queue, so we let the appnavigator update b4 changing route
    observeOn(asyncScheduler),
    map(([payload, _]) => {
      history.push(payload)
    }),
    dispatchNavigatedAction
  )
}

// blocked from user to navigation straith to signup steps url
export const blockUserToRedirectToSignupStepsUrl = action$ => {
  return action$.pipe(
    getPayload(Actions.LOCATION_CHANGED),
    filter(
      payload =>
        payload?.action === 'POP' &&
        payload?.isFirstRendering &&
        // only for signup routes, except signup root
        isSignupRoute(payload?.location?.pathname) &&
        payload?.location?.pathname !== routes.SIGN_UP
    ),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.SIGN_UP))
    )
  )
}

export const blockUserToRedirectToPaymentSuccess = action$ => {
  return action$.pipe(
    getPayload(Actions.LOCATION_CHANGED),
    filter(
      payload =>
        payload?.action === 'POP' &&
        payload?.isFirstRendering &&
        payload?.location?.pathname === routes.PAYMENT_SUCCESS
    ),
    switchMap(() => of(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT)))
  )
}

// blocked from user to navigation straith to bot url / before visit page
export const blockUserToRedirectToBotUrl = action$ => {
  return action$.pipe(
    getPayload(Actions.LOCATION_CHANGED),
    filter(
      payload =>
        payload?.action === 'POP' &&
        payload?.isFirstRendering &&
        (isBotRoute(payload?.location?.pathname) ||
          payload?.location?.pathname === routes.BEFORE_VISIT)
    ),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.ROOT))
    )
  )
}

// on risk assessment bot we should show alert the user when he navigate somewhere outside the bot
// this is in order to prevent him for loosing data/leave in the middle of the process intake
// when we get navigation action from bot, we should block navigation (epic navigateTo) and dispatch terminate popup action
export const blockNavigateOutsideRiskAssessmentBot = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.NAVIGATED_TO),
    withLatestFrom(state$),
    filter(
      ([payload, { router }]) =>
        router?.location?.pathname === routes.RISK_ASSESSMENT_BOT &&
        payload !== routes.PROFILE_ROUTE.MVP_STATUS
    ), // only for bot routes!,
    switchMap(() =>
      of(
        Actions.createAction(Actions.TERMINATE_RISK_ASSESSMENT_POPUP, {
          isVisible: true
        })
      )
    )
  )
}

export const isPhoneNumberVerificationSuccess = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.SIGNUP_PHONE_NUMBER_VERIFICATION_PROCESS_COMPLETED),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.CHOOSE_PASSWORD))
    )
  )
}

export const userDontHaveAnAccount = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.DONT_HAVE_AN_ACCOUNT),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.SIGN_UP))
    )
  )
}

export const talkToADoctor = action$ => {
  return action$.pipe(
    getPayload(Actions.SHOW_TALK_TO_A_DOCTOR_REQUESTED),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.BOT))
  )
}

export const inValidIpRecieved = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.INVALIDATE_COUNTRY_RECEIVED),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.INVALID_IP))
  )
}

export const videoCallStarted = (action$, state$) => {
  // We could listen to video_call_inited, but since it comes
  // after a couple api calls, i would rather first put the user in the waiting room
  // and then fetch
  return action$.pipe(
    getPayload(Actions.JOIN_VIDEO_RESOLVED),
    filter(status => status === 'approved'),
    withLatestFrom(state$),
    mapTo(Actions.createAction(Actions.VIDEO_CALL_DIALOG_VISIBLE, true))
  )
}

export const redirectToOneTimeLogin = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.REDIRECT_TO_ONE_TIME_LOGIN),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.ONE_TIME_LOGIN))
    )
  )
}

export const redirectToChat = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.REDIRECT_TO_HOME),
    switchMap(() =>
      of(
        Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT),
        Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED)
      )
    )
  )
}
export const pullEtaAfterPrimary = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.BOT_WIZARD_ENDED),
    switchMap(() =>
      of(Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED))
    )
  )
}
export const redirectToChatFromPPFFlow = (action$, state$) =>
  action$.pipe(
    ofType(Actions.SIGNIN_REQUESTED_FROM_PPF_FLOW),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT))
  )

export const redirectToAccountPage = (action$, state$) =>
  action$.pipe(
    ofType(Actions.REDIRECT_TO_ACCOUNTS_PAGE),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.MENU_ROUTE.ACCOUNT))
    )
  )

export const redirectToPaymentCancel = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.REDIRECT_TO_PAYMENT_CANCEL),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PLANS))
    )
  )
}

export const redirectToResetPassword = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.FORGET_PASSWORD_REQUESTED),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.RESET_PASSWORD))
    )
  )
}

export const redirectToResetAndChoosePassword = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.OTP_CODE_VERIFY_RECEIVED),
    switchMap(({ route }) =>
      of(Actions.createAction(Actions.NAVIGATED_TO, route))
    )
  )
}

export const redirectToHomeAfterPayment = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.REDIRECT_TO_PAYMENT_SUCCESS),
    withLatestFrom(state$),
    filter(
      ([_, { appointments }]) =>
        !appointments?.pendingAppointment && isLoggedIn()
    ),
    switchMap(() => of(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT)))
  )
}

export const redirectToPaymentSuccessAfterPaymentForUnautentictedUser =
  action$ => {
    return action$.pipe(
      getPayload(Actions.REDIRECT_TO_PAYMENT_SUCCESS),
      filter(() => !isLoggedIn()),
      switchMap(() =>
        of(Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_SUCCESS))
      )
    )
  }

export const redirectToBeforeVisitAfterPayment = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.REDIRECT_TO_PAYMENT_SUCCESS),
    withLatestFrom(state$),
    filter(([_, { appointments }]) => !!appointments?.pendingAppointment),
    switchMap(([_, { appointments }]) => {
      return of(
        Actions.createAction(
          Actions.SELECTED_APPOINTMENT_CHANGED,
          appointments?.pendingAppointment
        )
      )
    })
  )
}

export const redirectToPaymentSuccessPPF = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.PAYMENT_SUCCESS_FROM_PPF),
    switchMap(() =>
      of(
        Actions.createAction(
          Actions.NAVIGATED_TO,
          routes.EXTERNAL_PAYMENT_SUCCESS_PAGE
        )
      )
    )
  )
}

export const changePaymentMethod = action$ => {
  return action$.pipe(
    getPayload(Actions.CHANGE_PAYMENT_METHOD),
    mapTo(Actions.createAction(Actions.BILLING_PORTAL_REQUESTED))
  )
}

export const addPaymentMethod = action$ => {
  return action$.pipe(
    getPayload(Actions.ADD_PAYMENT_METHOD),
    mapTo(Actions.createAction(Actions.BILLING_PORTAL_REQUESTED))
  )
}

export const redirectToBlockMobileWebUsers = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.IS_MOBILE_WEB_USER),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.BLOCK_MOBILE_WEB))
    )
  )
}

export const redirectToBlockUnder18Users = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.IS_USER_UNDER_18),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.BLOCK_UNDER_18_USER))
    )
  )
}

export const redirectToSpecialityServices = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.REDIRECT_TO_SPECIALTY_SERVICES),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.SPECIALITY_SERVICES))
    )
  )
}

// check if user has pending initial appointment -> if yes, redirect to before visit primary page
export const redirectToBeforeVisitPending = (action$, state$) => {
  return action$.pipe(
    getPayload(
      Actions.PRIMARY_CLINIC_CLICKED,
      Actions.HYPERTENSION_CLICKED,
      Actions.MENTALHEALTH_CLICKED,
      Actions.DIABETES_CLICKED
    ),
    withLatestFrom(state$),
    filter(([_, state]) =>
      filterPendingInitialAppointment(state.appointments?.appointments)
    ),
    switchMap(([_, state]) => {
      let appointment = filterPendingInitialAppointment(
        state.appointments?.appointments
      )
      return of(
        Actions.createAction(Actions.SELECTED_APPOINTMENT_CHANGED, appointment)
      )
    })
  )
}

// check that the user hasn't pending initial appointment.
export const navToPrimaryGetCare = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.PRIMARY_CLINIC_CLICKED),
    withLatestFrom(state$),
    filter(
      ([_, state]) =>
        !filterPendingInitialAppointment(state.appointments?.appointments)
    ),
    switchMap(() => {
      return of(Actions.createAction(Actions.NAVIGATED_TO, routes.GET_CARE))
    })
  )
}

// check if user hasn't pending initial appointment.
export const redirectToHTNBotPreviewPage = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.HYPERTENSION_CLICKED),
    withLatestFrom(state$),
    filter(
      ([_, state]) =>
        !filterPendingInitialAppointment(state.appointments?.appointments)
    ),
    switchMap(([_, state]) => {
      // if user has scheduled appointment for htn clinic, redirect to before visit page for this appointment
      const clinicQueueAppointment = filterQueueAppointmentsClinic(
        state.appointments?.appointments,
        clinicTypes.HTN
      )
      if (clinicQueueAppointment.length > 0) {
        return of(
          Actions.createAction(
            Actions.SELECTED_APPOINTMENT_CHANGED,
            clinicQueueAppointment[0]
          )
        )
      }
      // if user has no scheduled appointment for htn clinic, redirect to get htn bot preview page
      return of(
        Actions.createAction(Actions.NAVIGATED_TO, routes.HTN_BOT_PREVIEW)
      )
    })
  )
}

// check if user hasn't pending initial appointment.
export const redirectToMhBotPreviewPage = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.MENTALHEALTH_CLICKED),
    withLatestFrom(state$),
    filter(
      ([_, state]) =>
        !filterPendingInitialAppointment(state.appointments?.appointments)
    ),
    switchMap(([_, state]) => {
      // if user has scheduled appointment for mh clinic, redirect to before visit page for this appointment
      const clinicQueueAppointment = filterQueueAppointmentsClinic(
        state.appointments?.appointments,
        clinicTypes.MENTAL
      )
      const isHmoMember = getIsHmoMember(state.profile?.userDetails)
      if (clinicQueueAppointment.length > 0) {
        return of(
          Actions.createAction(
            Actions.SELECTED_APPOINTMENT_CHANGED,
            clinicQueueAppointment[0]
          )
        )
      }
      // if user has no scheduled appointment for mh clinic and its hmo member, redirect to get mh bot
      if (isHmoMember) {
        return of(Actions.createAction(Actions.MENTALHEALTH_BOT_INITED))
      }
      // if user has no scheduled appointment for mh clinic and its no hmo member, redirect to get mh bot preview page
      return of(
        Actions.createAction(Actions.NAVIGATED_TO, routes.MH_BOT_PREVIEW)
      )
    })
  )
}

// check if user hasn't pending initial appointment.
export const redirectToDmBotPreviewPage = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.DIABETES_CLICKED),
    withLatestFrom(state$),
    filter(
      ([_, state]) =>
        !filterPendingInitialAppointment(state.appointments?.appointments)
    ),
    switchMap(([_, state]) => {
      // if user has scheduled appointment for dm clinic, redirect to before visit page for this appointment
      const clinicQueueAppointment = filterQueueAppointmentsClinic(
        state.appointments?.appointments,
        clinicTypes.DIABETES
      )
      if (clinicQueueAppointment.length > 0) {
        return of(
          Actions.createAction(
            Actions.SELECTED_APPOINTMENT_CHANGED,
            clinicQueueAppointment[0]
          )
        )
      }
      // if user has no scheduled appointment for dm clinic, redirect to get dm bot preview page
      return of(
        Actions.createAction(Actions.NAVIGATED_TO, routes.DM_BOT_PREVIEW)
      )
    })
  )
}

export const redirectToMhBotInfoPage = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.REDIRECT_TO_MH_BOT_INFO),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.MH_BOT_INFO))
    )
  )
}

export const clinicBotStart = action$ =>
  action$.pipe(
    getPayload(
      Actions.HTN_BOT_STARTED,
      Actions.DIABETES_BOT_STARTED,
      Actions.MENTALHEALTH_BOT_STARTED,
      Actions.OPEN_MH_FOLLOW_UP_BOT_SCREEN,
      Actions.OPEN_HTN_FOLLOW_UP_BOT_SCREEN,
      Actions.OPEN_DM_FOLLOW_UP_BOT_SCREEN,
      Actions.OPEN_PRIMARY_HMO_FOLLOW_UP_BOT_SCREEN
    ),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.GET_CARE))
  )

export const BotAborted = action$ =>
  action$.pipe(
    ofType(Actions.NO_SHIFTS, Actions.BOT_ABORTED),
    mapTo(Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.ROOT))
  )

export const redirectAfterRescheduleAppointment = action$ => {
  return action$.pipe(
    ofType(Actions.SHOW_RESCHEDULE_APPOINTMENT),
    mapTo(Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.GET_CARE))
  )
}

export const redirectToProfileResetPassword = action$ => {
  return action$.pipe(
    getPayload(Actions.PROFILE_RESET_PASSWORD),
    switchMap(() =>
      of(
        Actions.createAction(
          Actions.NAVIGATED_TO,
          routes.PROFILE_ROUTE.RESET_PASSWORD
        )
      )
    )
  )
}

export const redirectToPaymentDetails = action$ => {
  return action$.pipe(
    getPayload(Actions.PAYMENT_DETAILS),
    switchMap(() =>
      of(
        Actions.createAction(
          Actions.NAVIGATED_TO,
          routes.PROFILE_ROUTE.PAYMENT_DETAILS
        )
      )
    )
  )
}

export const redirectToConsents = action$ => {
  return action$.pipe(
    getPayload(Actions.CONSENTS_CLICK),
    switchMap(() =>
      of(
        Actions.createAction(
          Actions.NAVIGATED_TO,
          routes.PROFILE_ROUTE.MY_CONSENTS
        )
      )
    )
  )
}

export const redirectToDiscountCard = action$ => {
  return action$.pipe(
    getPayload(Actions.DISCOUNT_CARD_CLICK),
    switchMap(() => window.open(config.couponsURL))
  )
}

export const redirectToPaymentPage = actions$ =>
  actions$.pipe(
    getPayload(Actions.CHECKOUT_SESSION_RECEIVED),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PAGE))
  )

export const redirectToWalnutPage = actions$ =>
  actions$.pipe(
    getPayload(Actions.CHECKOUT_WALNUT_RECEIVED),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.WALNUT_PAGE))
  )

export const redirectToSupport = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.SUPPORT_CLICK),
    withLatestFrom(state$),
    switchMap(([_, { profile }]) =>
      window.open(getContactSupportUrl(getIsHmoMember(profile?.userDetails)))
    )
  )
}

export const redirectToFAQ = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.FAQ_CLICK),
    withLatestFrom(state$),
    filter(([_, { profile }]) => getIsHmoMember(profile?.userDetails)),
    switchMap(([_, { app }]) => window.open(app?.links?.FAQ || ''))
  )
}

export const redirectToFAQTelehealth = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.FAQ_CLICK),
    withLatestFrom(state$),
    filter(([_, { profile }]) => !getIsHmoMember(profile?.userDetails)),
    switchMap(([_, { app }]) => window.open(app?.links?.FAQ_TELE || ''))
  )
}

export const redirectToMobileSupport = action$ => {
  return action$.pipe(
    getPayload(Actions.MY_FAMILY_CLICK),
    switchMap(() =>
      of(Actions.createAction(Actions.NAVIGATED_TO, routes.BLOCK_MOBILE_WEB))
    )
  )
}

export const redirectToPlans = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.ACCOUNT_PRESSED),
    withLatestFrom(state$),
    map(() =>
      state$.value.payment.my_subscriptions &&
      state$.value.payment.my_subscriptions?.[0]
        ? Actions.createAction(Actions.NAVIGATED_TO, routes.MENU_ROUTE.ACCOUNT)
        : Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PLANS)
    )
  )
}

export const slotExiped = action$ => {
  return action$.pipe(
    ofType(Actions.SLOT_EXPIRED),
    switchMap(() =>
      hasAuthToken().pipe(
        mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT))
      )
    )
  )
}

const isPaymentDetailsRoute = state =>
  state.router.location.pathname === routes.PROFILE_ROUTE.PAYMENT_DETAILS

const isGetCareRoute = state =>
  state.router.location.pathname === routes.GET_CARE

export const redirectToBuyPlan = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_STATE_RECEIVED),
    filter(({ is_valid_state }) => is_valid_state),
    withLatestFrom(state$),
    filter(
      ([_, state]) => !isPaymentDetailsRoute(state) && !isGetCareRoute(state)
    ),
    switchMap(([_, state]) => {
      const { selectedPlan } = state.payment
      const userState = validateZipcode(state.auth.locationVerification.zipcode)
      const isBlackListedState =
        selectedPlan?.blackListedStates?.includes(userState)
      if (userState && isBlackListedState) {
        return of(
          Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED),
          Actions.createAction(Actions.SHOW_NO_SUBSCRIPTIONS_MODAL)
        )
      }
      return of(
        Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED),
        Actions.createAction(Actions.PROCEED_TO_CHECKOUT_PRESSED, {
          customerCoupon: state.payment?.customerCoupon,
          selectedPlan: state.payment?.selectedPlan
        }),
        Actions.createAction(Actions.CHECKOUT_SESSION_REQUESTED, {
          customerCoupon: state.payment?.customerCoupon
            ? state.payment?.customerCoupon
            : null,
          selectedPlan: state.payment?.selectedPlan
        }),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          eventName: 'go_to_checkout_web',
          data: {
            selectedPlan: state.payment?.selectedPlan,
            customerCoupon: state.payment?.customerCoupon
          }
        })
      )
    })
  )

export const paymentDetailsLocation = (action$, state$) =>
  action$.pipe(
    ofType(Actions.VALIDATE_STATE_RECEIVED),
    withLatestFrom(state$),
    filter(([_, state]) => isPaymentDetailsRoute(state)),
    switchMap(([_, state]) => {
      return of(
        Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED),
        Actions.createAction(Actions.GENERATE_UPDATE_PAYMENT_CARD_REQUESTED)
      )
    })
  )

export const closeBot = action$ => {
  return action$.pipe(
    getPayload(Actions.BOT_WIZARD_CLOSED),
    mapTo(Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.ROOT))
  )
}

export const openVcc = action$ =>
  action$.pipe(
    ofType(Actions.VIEW_VCC_CLICKED),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.MENU_ROUTE.DEBIT_CARD)
    )
  )

export const goBackHomePaymentCancel = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.PAYMENT_FLOW_CANCELLED),
    withLatestFrom(state$),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT))
  )
}

export const navToSpecialtyServices = action$ =>
  action$.pipe(
    getPayload(Actions.SPECIALTY_SERVICES_CLICKED),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.SPECIALITY_SERVICES)
    )
  )

export const redirectToBeforeVisitAfterWizardEnd = action$ =>
  action$.pipe(
    getPayload(Actions.WIZARD_ENDED),
    mapTo(
      Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.BEFORE_VISIT)
    )
  )

export const beforeVisitView = action$ =>
  action$.pipe(
    getPayload(Actions.SELECTED_APPOINTMENT_CHANGED),
    mapTo(
      Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.BEFORE_VISIT)
    )
  )

export const initiateAudioVideoConnectivityTest = action$ =>
  action$.pipe(
    ofType(Actions.INITIATE_CONNECTIVITY_TEST),
    mergeMap(() => {
      return of(
        Actions.createAction(Actions.NAVIGATED_TO, routes.TEST_CONNECTIVITY)
      )
    })
  )

export const openUpdateMyPharmacyForOtherMember = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.UPDATE_MY_PHARMACY_PRESSED_FOR_OTHER_MEMBER),
    withLatestFrom(state$),
    filter(([_, state]) => isNeedToAskMyPharmacyUpdate(state)),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.PHARMACY)
    )
  )
}

export const openUpdateMyPharmacy = action$ => {
  return action$.pipe(
    ofType(Actions.UPDATE_MY_PHARMACY_PRESSED),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.PHARMACY)
    )
  )
}

export const openUpdateEmergencyContact = action$ => {
  return action$.pipe(
    ofType(Actions.UPDATE_EMERGENCY_CONTACT_PRESSED),
    mapTo(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.PROFILE_ROUTE.EMERGENCY_CONTACT
      )
    )
  )
}

export const openOpenAccountStatus = action$ => {
  return action$.pipe(
    ofType(Actions.ACCOUNT_STATUS_PRESSED),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.MENU_ROUTE.ACCOUNT))
  )
}

export const navToApptFromNotification = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.NAV_TO_APPT_BEFORE_VISIT_FROM_NOTIFICATION),
    withLatestFrom(state$),
    filter(
      ([{ payload }, state]) =>
        !!state?.appointments?.appointments?.find(
          appt => appt?.id === payload?.appointmentId
        )
    ),
    switchMap(([{ payload }, state]) => {
      const appointment = state?.appointments?.appointments?.find(
        appt => appt?.id === payload?.appointmentId
      )
      return of(
        Actions.createAction(Actions.SELECTED_APPOINTMENT_CHANGED, appointment)
      )
    })
  )
}

export const openUpdateGenderDetails = action$ => {
  return action$.pipe(
    ofType(Actions.UPDATE_GENDER_DETAILS_PRESSED),
    mapTo(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.PROFILE_ROUTE.GENDER_DETAILS
      )
    )
  )
}

export const redirectToProfileOnGenderUpdated = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.UPDATE_GENDER_DETAILS_RECEIVED),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.PROFILE)
    )
  )
}

export const navTtoMedicalNoteFromNotification = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.NAV_TO_MEDICAL_NOTE_FROM_NOTIFICATION),
    withLatestFrom(state$),
    filter(
      ([{ payload }, state]) =>
        !!state?.medicalInfo?.workNotes?.find(
          medicalNote =>
            medicalNote?.file?.toString() === payload?.medicalNoteFileId
        )
    ),
    switchMap(([{ payload }, _]) => {
      return of(
        Actions.createAction(
          Actions.NAVIGATED_TO,
          `${routes.MENU_ROUTE.DOCTOR_NOTES}/${payload?.medicalNoteFileId}`
        )
      )
    })
  )
}

export const navTtoPrescriptionFromNotification = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.NAVIGATE_TO_PRESCRIPTION),
    withLatestFrom(state$),
    filter(
      ([{ payload }, state]) =>
        !!state?.medicalInfo?.prescriptions?.find(
          prescription =>
            prescription?.file?.toString() === payload?.prescriptionFileId
        )
    ),
    switchMap(([{ payload }, _]) => {
      return of(
        Actions.createAction(
          Actions.NAVIGATED_TO,
          `${routes.MENU_ROUTE.PRESCRIPTION}/${payload?.prescriptionFileId}`
        )
      )
    })
  )
}

export const navToReferralResultFromNotification = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.NAV_TO_REFERRAL_RESULT_FROM_NOTIFICATIONS),
    withLatestFrom(state$),
    switchMap(([referralResultId, state]) => {
      const referralResult = state?.medicalInfo?.questReferralResults?.find(
        referralResult =>
          referralResult?.result?.toString() === referralResultId
      )

      if (!referralResult) {
        console.error(
          `Referral result with ID ${referralResultId} not found in questReferralResults array`
        )
        return of(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            MenuRoutes.REFERRAL_RESULTS
          )
        )
      }

      return of(
        Actions.createAction(Actions.NAV_TO_REFERRAL_RESULT, referralResult)
      )
    })
  )
}

export const navToReferralResultScreen = action$ => {
  return action$.pipe(
    getPayload(Actions.NAV_TO_REFERRAL_RESULT),
    switchMap(payload => {
      const url = MenuRoutes.REFERRAL_RESULTS
      const fileUrl = payload?.file || payload?.pdf
      if (fileUrl) {
        const link = uriEncodeSearchParams('', {
          shouldShowPdfByUrl: !!fileUrl
        })
        return of(
          Actions.createAction(Actions.VIEW_DOCUMENT_BY_URL, {
            pdf: fileUrl
          }),
          Actions.createAction(Actions.NAVIGATED_TO, `${url}/${0}/${link}`)
        )
      }
    })
  )
}

export const navToClinicReferralScreenFromScreenOrNotification = action$ => {
  return action$.pipe(
    getPayload(Actions.NAV_TO_CLINIC_REFERRAL),
    switchMap(clinicType => {
      const clinicName = convertClinicNames(clinicType)
      switch (clinicName) {
        case clinicTypes.DIABETES: {
          return of(Actions.createAction(Actions.DIABETES_CLICKED))
        }
        case clinicTypes.HTN: {
          return of(Actions.createAction(Actions.HYPERTENSION_CLICKED))
        }
        case clinicTypes.MENTAL: {
          return of(Actions.createAction(Actions.MENTALHEALTH_CLICKED))
        }
        default: {
          break
        }
      }
    })
  )
}

export const navToLabReferralFile = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.NAV_TO_LAB_REFERRAL),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const referral = state?.medicalInfo?.referrals?.find(
        labReferral => labReferral?.file?.toString() === payload?.fileId
      )

      if (!referral) {
        console.error(
          `Referral with ID ${payload?.fileId} not found in referrals array`
        )
        return of(
          Actions.createAction(Actions.NAVIGATED_TO, MenuRoutes.REFERRALS)
        )
      }

      const url = MenuRoutes.REFERRALS
      return of(
        Actions.createAction(Actions.NAVIGATED_TO, `${url}/${payload?.fileId}`)
      )
    })
  )
}

export const navToQuestReferralScreen = (action$, state$) => {
  return action$.pipe(
    ofType(Actions.NAV_TO_QUEST_REFERRAL),
    withLatestFrom(state$),
    filter(
      ([{ payload }, state]) =>
        !!state?.medicalInfo?.questReferrals?.find(
          questReferral =>
            questReferral?.order_id.toString() === payload?.questReferralOrderId
        )
    ),
    switchMap(([{ payload }, state]) => {
      const questReferral = state?.medicalInfo?.questReferrals?.find(
        questReferral =>
          questReferral?.order_id.toString() === payload?.questReferralOrderId
      )
      const pdfLink = questReferral?.lab_referral_pdf
      const link = uriEncodeSearchParams('', {
        shouldShowPdfByUrl: !!pdfLink
      })
      return of(
        Actions.createAction(Actions.VIEW_DOCUMENT_BY_URL, {
          pdf: questReferral?.lab_referral_pdf
        }),
        Actions.createAction(
          Actions.NAVIGATED_TO,
          `${MenuRoutes.REFERRALS}/${0}/${link}`
        )
      )
    })
  )
}

export const openUpdateContactInformation = action$ => {
  return action$.pipe(
    ofType(Actions.CONTACT_INFORMATION_PRESSED),
    mapTo(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.PROFILE_ROUTE.CONTACT_INFORMATION
      )
    )
  )
}

export const openCareTeamScreen = action$ => {
  return action$.pipe(
    ofType(Actions.CARE_TEAM_PRESSED),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.CARE_TEAM)
    )
  )
}

export const redirectToFindAProviderScreen = action$ =>
  action$.pipe(
    ofType(Actions.FIND_A_PROVIDER_CLICKED),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.SEARCH_PROVIDERS))
  )

// on boarding flow - redirect to home screen
export const redirectHomePageAfterSecretQuestions = action$ =>
  action$.pipe(
    ofType(Actions.SECRET_QUESTION_UPDATE_RECEIVED),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT))
  )

export const redirectToSettingsPreferencesScreen = action$ =>
  action$.pipe(
    ofType(Actions.SETTING_PREFERENCES_PRESSED),
    mapTo(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.PROFILE_ROUTE.SETTING_PREFERENCES
      )
    )
  )

export const redirectToCommunicationPreferencesScreen = action$ =>
  action$.pipe(
    ofType(Actions.COMMUNICATION_PREFERENCES_PRESSED),
    switchMap(() =>
      of(
        Actions.createAction(Actions.OPEN_COMMUNICATION_PREFERENCES_CONFIGURE),
        Actions.createAction(
          Actions.NAVIGATED_TO,
          routes.PROFILE_ROUTE.COMMUNICATION_PREFERENCES
        )
      )
    )
  )

export const redirectToChangePasswordScreen = action$ =>
  action$.pipe(
    ofType(Actions.CHANGE_PASSWORD_PRESSED),
    mapTo(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.PROFILE_ROUTE.RESET_PASSWORD
      )
    )
  )

export const redirectToLanguageScreen = action$ =>
  action$.pipe(
    ofType(Actions.LANGUAGE_PRESSED),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.LANGUAGE)
    )
  )

export const redirectToMvpStatusScreen = action$ =>
  action$.pipe(
    ofType(Actions.MVP_STATUS_PRESSED),
    mapTo(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.PROFILE_ROUTE.MVP_STATUS
      )
    )
  )

export const redirectToNhPage = action$ =>
  action$.pipe(
    ofType(
      Actions.NH_PAGE_PAYMENTS_PRESSED,
      Actions.NH_PAGE_DENTAL_PRESSED,
      Actions.NH_PAGE_DRUGS_PRESSED,
      Actions.NH_PAGE_PRESCRIPTIONS_PRESSED,
      Actions.NH_PAGE_REIMBURSEMENT_PRESSED,
      Actions.NH_PAGE_CLAIMS_DRUGS_PRESSED,
      Actions.NH_PAGE_CLAIMS_GRIEVANCES_DRUGS_PRESSED
    ),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.NH_PAGE))
  )

export const redirectToInviteAppPage = action$ =>
  action$.pipe(
    ofType(Actions.INVITE_HMO_MEMBER_TO_DOWNLOAD_APP_PRESSED),
    mapTo(
      Actions.createAction(Actions.NAVIGATION_FROM_BOT, routes.INVITE_TO_APP)
    )
  )

export const redirectToCareTeamAfterUpdatePcp = action$ =>
  action$.pipe(
    ofType(Actions.UPDATE_PCP_RECEIVED, Actions.UPDATE_PCP_FAILED),
    mapTo(
      Actions.createAction(Actions.NAVIGATED_TO, routes.PROFILE_ROUTE.CARE_TEAM)
    )
  )

export const redirectNoSubscriptionUser = (action$, state$) =>
  action$.pipe(
    ofType(Actions.PAYMENT_METHOD_FETCH_FAILED),
    withLatestFrom(state$),
    filter(([_, { profile }]) => !getIsHmoMember(profile?.userDetails)),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PLANS))
  )
